import { useState, type FormEvent } from "react";
import {
  errorMessage,
  useAccounts,
  useCreateAccount,
  useReplaceToken,
  useTestAccount,
  type Account,
  type AccountStatus,
} from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import { timeAgo } from "@/lib/format";
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Mono,
  Skeleton,
  StatusDot,
  useToast,
} from "@/ui";
import "./pages.css";

const STATUS: Record<AccountStatus, { dot: "live" | "paused" | "dead"; label: string }> = {
  active: { dot: "live", label: "Active" },
  paused: { dot: "paused", label: "Paused" },
  rejected: { dot: "dead", label: "Token rejected" },
};

type Dialog = { kind: "add" } | { kind: "replace"; account: Account } | null;

export function AccountsPage() {
  useDocumentTitle("Account & token");
  const accounts = useAccounts();
  const [dialog, setDialog] = useState<Dialog>(null);

  const items = accounts.data?.items ?? [];
  return (
    <div className="page">
      <PageHeader
        title="Account & token"
        description="The user account the listener reads with. The token is stored encrypted and never shown again."
        actions={
          <Button variant={items.length ? "secondary" : "primary"} onClick={() => setDialog({ kind: "add" })}>
            Add account
          </Button>
        }
      />
      <ErrorBanner error={accounts.error} onRetry={() => accounts.refetch()} />
      {accounts.isLoading ? (
        <Card>
          <Skeleton lines={3} />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title="No account yet"
            description="The listener needs a user token to read source servers."
            action={
              <Button variant="primary" onClick={() => setDialog({ kind: "add" })}>
                Add account
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="stack">
          {items.map((a) => (
            <AccountCard
              key={a.id}
              account={a}
              onReplace={() => setDialog({ kind: "replace", account: a })}
            />
          ))}
        </div>
      )}
      <TokenDialog dialog={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}

function AccountCard({ account, onReplace }: { account: Account; onReplace: () => void }) {
  const test = useTestAccount();
  const toast = useToast();
  const st = STATUS[account.status];

  const runTest = () =>
    test.mutate(account.id, {
      onSuccess: (r) =>
        r.ok
          ? toast.show({
              tone: "success",
              title: "Token works",
              message: r.discord_user ? `Signed in as ${r.discord_user.username}` : undefined,
            })
          : toast.show({ tone: "danger", title: "Token test failed", message: r.error ?? undefined }),
      onError: (e) => toast.show({ tone: "danger", title: "Test failed", message: errorMessage(e) }),
    });

  return (
    <Card
      title={account.label}
      actions={
        <>
          <Button size="sm" variant="secondary" onClick={runTest} loading={test.isPending}>
            Test
          </Button>
          <Button size="sm" variant={account.status === "rejected" ? "primary" : "ghost"} onClick={onReplace}>
            Replace token
          </Button>
        </>
      }
    >
      <div className="account-row">
        <dl className="kv">
          <dt>Status</dt>
          <dd>
            <StatusDot status={st.dot} label={st.label} />
          </dd>
          <dt>Token</dt>
          <dd>
            <Mono value={account.token_masked} copy={false} />
          </dd>
          <dt>Last ready</dt>
          <dd>{timeAgo(account.last_ready_at)}</dd>
        </dl>
      </div>
      {account.status === "rejected" ? (
        <div className="banner" role="alert" style={{ marginTop: "var(--space-4)" }}>
          Discord rejected this token. The listener is stopped until you replace it.
        </div>
      ) : null}
    </Card>
  );
}

function TokenDialog({ dialog, onClose }: { dialog: Dialog; onClose: () => void }) {
  const create = useCreateAccount();
  const replace = useReplaceToken();
  const toast = useToast();
  const [label, setLabel] = useState("Main listener");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);

  const busy = create.isPending || replace.isPending;
  const close = () => {
    setToken("");
    setError(null);
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (dialog?.kind === "replace") {
        await replace.mutateAsync({ id: dialog.account.id, token: token.trim() });
        toast.show({ tone: "success", title: "Token replaced", message: "Listener restarting" });
      } else {
        await create.mutateAsync({ label: label.trim(), token: token.trim() });
        toast.show({ tone: "success", title: "Account added" });
      }
      close();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const formId = "token-form";
  return (
    <Modal
      open={dialog !== null}
      onClose={close}
      title={dialog?.kind === "replace" ? `Replace token · ${dialog.account.label}` : "Add account"}
      description="Paste the user token. It is encrypted at rest and only shown masked."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={busy}
            disabled={!token.trim() || (dialog?.kind === "add" && !label.trim())}
          >
            {dialog?.kind === "replace" ? "Replace token" : "Add account"}
          </Button>
        </>
      }
    >
      <form id={formId} className="stack" onSubmit={submit}>
        {dialog?.kind === "add" ? (
          <Input label="Label" value={label} onChange={(e) => setLabel(e.target.value)} />
        ) : null}
        <Input
          label="Token"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          error={error ?? undefined}
          hint="Never share this token. Relay never displays it again."
        />
      </form>
    </Modal>
  );
}
